import * as z from 'zod/v4';

import { drawStateClientEnum } from '../draw/entry.js';
import { apiErrorSchema } from '../error/entry.js';
import { formData, repeatableOfType } from '../utils/entry.js';
import { sessionStoreBaseSchema, sessionStoreDataBaseSchema } from './sessionStore.js';

/**
 * JSON schema that represents wager settings.
 * Handles fallback values if data is malicious or undefined
 */
const wagerSettingsJsonSchema = z.object({
  correction: z.array(drawStateClientEnum).optional().catch(undefined),
});

export type WagerSettings = z.infer<typeof wagerSettingsJsonSchema>;
export type WagerSettingsReturnType = Omit<WagerSettings, 'correction'> & {
  correction: NonNullable<WagerSettings['correction']>;
};

const safeJSONParse = (value: string): unknown => {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return {};
  }
};

/**
 * JSON schema to parse data from session storage.
 */
export const sessionStoreWagerSettingsSchema = z.object({
  ...sessionStoreBaseSchema.shape,
  data: z.string().transform((value) => {
    const parsed = safeJSONParse(value);

    const base = sessionStoreDataBaseSchema.parse(parsed);
    const settings = wagerSettingsJsonSchema.parse(parsed);

    return {
      ...base,
      ...settings,
    };
  }),
});

export const wagerSettingsResponse = {
  parse: (data: unknown) => z.union([sessionStoreWagerSettingsSchema, apiErrorSchema]).parse(data),
} as const;

export const createWagerSettingsResponse = {
  // Any object is considered Ok since the payload isn't interesting
  parse: (data: unknown) => z.union([apiErrorSchema, z.object({})]).parse(data),
} as const;

/**
 * Parses HTML FormData to the corresponding JSON schema
 */
export const wagerSettingsFormDataSchema = formData({
  correction: repeatableOfType(drawStateClientEnum).catch([]),
}).transform((data) => {
  return {
    correction: data.correction,
  } satisfies WagerSettings;
});

export const wagerSettingsFormData = {
  parse: (data: unknown) => wagerSettingsFormDataSchema.parse(data),
} as const;
